const STOCK_FIELDS = [
  "totalAvailable",
  "totalQuantity",
  "availableQuantity",
  "available",
  "countInStock",
  "inventory",
  "quantity",
  "maxAvailable",
  "stockQuantity",
  "stock",
];

const OUT_OF_STOCK_STATUSES = ["out_of_stock", "out-of-stock", "unavailable"];
const IN_STOCK_STATUSES = ["in_stock", "in-stock", "available"];

const isObject = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const getAvailabilitySource = (product) => {
  if (!isObject(product)) return product;

  const nestedProduct =
    (isObject(product.product) && product.product) ||
    (isObject(product.productData) && product.productData) ||
    (isObject(product.productId) && product.productId) ||
    null;
  const nestedVariant =
    (isObject(product.selectedVariant) && product.selectedVariant) ||
    (isObject(product.variant) && product.variant) ||
    null;

  if (!nestedProduct && !nestedVariant) return product;

  return {
    ...product,
    ...(nestedProduct || {}),
    ...(nestedVariant || {}),
  };
};

const toNumber = (value) => {
  if (value == null || value === "") return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toNumericStockValue = (value) => {
  if (typeof value === "boolean") return null;
  return toNumber(value);
};

const getStockObjectQuantity = (stock) => {
  if (Array.isArray(stock)) {
    return stock.reduce((sum, stockItem) => {
      const itemQuantity = getStockObjectQuantity(stockItem);
      return sum + (itemQuantity ?? 0);
    }, 0);
  }

  if (!isObject(stock)) return null;

  const qtyIn = toNumber(stock.qtyIn);
  const qtyOut = toNumber(stock.qtyOut) ?? 0;
  const qtyReserved = toNumber(stock.qtyReserved) ?? 0;

  if (qtyIn !== null || hasOwn(stock, "qtyOut") || hasOwn(stock, "qtyReserved")) {
    return Math.max(0, (qtyIn ?? 0) - qtyOut - qtyReserved);
  }

  return getProductAvailableQuantity(stock);
};

export const hasProductAvailabilityData = (product) => {
  const source = getAvailabilitySource(product);
  if (!isObject(source)) return false;

  if (isAvailabilityStatus(normalizeStatus(source.status))) return true;

  if (
    typeof source.inStock === "boolean" ||
    typeof source.available === "boolean" ||
    typeof source.isAvailable === "boolean"
  ) {
    return true;
  }

  if (
    STOCK_FIELDS.some(
      (field) => hasOwn(source, field) && source[field] != null && source[field] !== "",
    )
  ) {
    return true;
  }

  const variants = getProductVariants(source);
  return variants.some(hasProductAvailabilityData);
};

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isAvailabilityStatus = (status) =>
  OUT_OF_STOCK_STATUSES.includes(status) || IN_STOCK_STATUSES.includes(status);

const getProductVariants = (product) =>
  Array.isArray(product?.variants)
    ? product.variants
    : Array.isArray(product?.options)
      ? product.options
      : [];

export const getProductAvailableQuantity = (product) => {
  const source = getAvailabilitySource(product);
  if (!source) return 0;

  const status = normalizeStatus(source.status);
  if (OUT_OF_STOCK_STATUSES.includes(status)) {
    return 0;
  }

  if (
    source.inStock === false ||
    source.available === false ||
    source.isAvailable === false
  ) {
    return 0;
  }

  for (const field of STOCK_FIELDS) {
    if (field === "stock") {
      const stockQuantity = getStockObjectQuantity(source[field]);
      if (stockQuantity !== null) return stockQuantity;
    }

    const value = toNumericStockValue(source[field]);
    if (value !== null) return value;
  }

  const variants = getProductVariants(source);

  if (variants.length) {
    return variants.reduce(
      (sum, variant) => sum + getProductAvailableQuantity(variant),
      0,
    );
  }

  if (typeof source.inStock === "boolean") return source.inStock ? 1 : 0;
  if (typeof source.available === "boolean") return source.available ? 1 : 0;
  if (typeof source.isAvailable === "boolean") {
    return source.isAvailable ? 1 : 0;
  }

  if (IN_STOCK_STATUSES.includes(status)) return 1;

  return 0;
};

export const isProductInStock = (product) =>
  getProductAvailableQuantity(product) > 0;

export const sortProductsByAvailability = (products) => {
  if (!Array.isArray(products)) return [];

  return products
    .map((product, index) => ({
      product,
      index,
      stockRank: isProductInStock(product) ? 0 : 1,
    }))
    .sort((left, right) => {
      if (left.stockRank !== right.stockRank) {
        return left.stockRank - right.stockRank;
      }

      return left.index - right.index;
    })
    .map(({ product }) => product);
};
